// src/Views/CartView.js
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { CartContext } from '../contexts/CartContext';
import { AuthContext } from '../contexts/AuthContext';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS, SPACING_TIGHT, SPACING_COMPACT, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP, SPACING_SECTION, SPACING_HERO, SPACING_PAGE } from '../constants/layout';
import { formatPrice } from '../utils/priceFormatter';

function CartView() {
  const {
    items,
    removeFromCart,
    updateQuantity,
    cartSubtotal,
    cartTax,
    estimatedTotal,
    products,
    getCoverImage,
  } = useContext(CartContext);

  // Get currency from first product in cart, or default to USD
  const currency = items.length > 0
    ? products.find(p => p.id === items[0].productID)?.priceInfo?.currency || 'USD'
    : 'USD';

  // Check if cart is empty
  if (items.length === 0) {
    return (
      <div className="cart-container">
        <div className="empty-cart">
          <div className="empty-icon">
            <span className="material-icons">shopping_cart</span>
          </div>
          <h2 className="empty-title">Your Cart Is Empty</h2>
          <p className="empty-description">
            You haven't added any items to your cart yet.
          </p>
          <Link to="/" className="continue-shopping-button">
            Continue Shopping
          </Link>
        </div>

        <style jsx>{`
          .cart-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: 0 ${SPACING_CARD};
            min-height: calc(100vh - 200px);
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .empty-cart {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: ${SPACING_GROUP};
            text-align: center;
          }

          .empty-icon {
            margin-bottom: ${SPACING_GROUP};
            opacity: 0.5;
          }

          .empty-icon .material-icons {
            font-size: 80px;
            color: var(--color-text-secondary);
          }

          .empty-title {
            font-size: 1.5rem;
            font-weight: 700;
            margin: 0 0 ${SPACING_BUTTON} 0;
            color: var(--color-text-primary);
          }

          .empty-description {
            font-size: 1rem;
            color: var(--color-text-secondary);
            margin: 0 0 ${SPACING_SECTION} 0;
          }

          .continue-shopping-button {
            display: inline-block;
            padding: ${SPACING_BUTTON} ${SPACING_GROUP};
            background-color: var(--color-accent);
            color: var(--color-accent-text);
            text-decoration: none;
            border-radius: ${BORDER_RADIUS};
            font-weight: 600;
            transition: background-color 0.2s ease;
          }

          .continue-shopping-button:hover {
            background-color: var(--color-accent-hover);
          }

          @media (max-width: 600px) {
            .section-title {
              font-size: 1.75rem;
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <div className="cart-header">
        <h1 className="section-title">Cart</h1>
      </div>

      <div className="cart-items-section">
        {items.map(item => {
          // Look up product data
          const product = products.find(p => p.id === item.productID);
          if (!product) return null;

          // Get cover image (similar to iOS ImageItemsManager)
          const coverImage = getCoverImage(product);

          // Get currency from product
          const productCurrency = product.priceInfo?.currency || 'USD';

          // Calculate base price
          const basePrice = product.priceInfo?.price || 0;

          // Calculate modifiers total
          const modifiersTotal = (item.selectedModifiers || []).reduce((total, mod) => {
            const group = product.modifierGroups?.find(g => g.id === mod.groupID);
            const option = group?.modifierOptions?.find(o => o.id === mod.optionID);
            return total + (option?.priceInfo?.price || 0);
          }, 0);

          // Unit price (base + modifiers)
          const unitPrice = basePrice + modifiersTotal;

          // Line total
          const lineTotal = unitPrice * item.quantity;

          return (
            <div key={item.id} className="cart-item">
              <div className="item-content">
                {coverImage?.imageURL ? (
                  <div className="item-image-wrapper">
                    <img
                      src={coverImage.imageURL}
                      alt={product.productName}
                      className="item-image"
                    />
                  </div>
                ) : (
                  <div className="item-image-wrapper">
                    <div className="product-placeholder">
                      <span className="placeholder-icon">📦</span>
                    </div>
                  </div>
                )}

                <div className="item-details">
                  <Link to={`/product/${item.productID}`} className="item-name">
                    {product.productName}
                  </Link>

                  {item.selectedModifiers && item.selectedModifiers.length > 0 && (
                    <div className="item-modifiers">
                      {item.selectedModifiers.map((mod, idx) => {
                        const group = product.modifierGroups?.find(g => g.id === mod.groupID);
                        const option = group?.modifierOptions?.find(o => o.id === mod.optionID);
                        return (
                          <span key={idx} className="modifier-text">
                            {option?.title || 'Unknown modifier'}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <div className="item-meta">
                    <span className="meta-text">
                      Quantity {item.quantity}
                    </span>
                    <span className="meta-separator"> | </span>
                    <span className="meta-text">
                      Item Price {formatPrice(unitPrice, productCurrency)}
                    </span>
                  </div>

                  <div className="item-actions">
                    <div className="quantity-controls">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="quantity-button"
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className="quantity-display">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="quantity-button"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="remove-button"
                      aria-label="Remove item from cart"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="trash-icon"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="item-total">
                  {formatPrice(lineTotal, productCurrency)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="cart-summary">
        <div className="summary-card">
          <div className="summary-row">
            <span className="summary-label">Cart Subtotal</span>
            <span className="summary-value">{formatPrice(cartSubtotal, currency)}</span>
          </div>

          <div className="summary-row">
            <span className="summary-label">Estimated Tax</span>
            <span className="summary-value">{formatPrice(cartTax, currency)}</span>
          </div>

          <div className="summary-row total-row">
            <span className="summary-label">Order Total</span>
            <span className="summary-value">{formatPrice(estimatedTotal, currency)}</span>
          </div>

          <button className="checkout-button" disabled>
            Check Out
          </button>

          <p className="checkout-note">Checkout functionality coming soon</p>
        </div>
      </div>

      <style jsx>{`
        .cart-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_HERO} ${SPACING_CARD} ${SPACING_PAGE};
        }

        .cart-header {
          margin-bottom: ${SPACING_SECTION};
        }

        .section-title {
          font-size: 2.25rem;
          font-family: var(--font-family-sans);
          font-weight: 800;
          margin: 0;
          text-transform: uppercase;
          color: var(--color-text-primary);
        }

        .cart-items-section {
          margin-bottom: ${SPACING_SECTION};
        }

        .cart-item {
          background-color: var(--color-card-background);
          border: 1px solid var(--color-border);
          border-radius: ${SPACING_COMPACT};
          padding: ${SPACING_CARD_BODY};
          margin-bottom: ${SPACING_CARD};
          overflow: hidden;
        }

        .item-content {
          display: flex;
          gap: ${SPACING_CARD};
        }

        .item-image-wrapper {
          flex-shrink: 0;
          width: 100px;
          height: 100px;
          overflow: hidden;
          background-color: var(--color-surface-muted);
        }

        .item-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .product-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--color-border);
        }

        .placeholder-icon {
          font-size: 32px;
          opacity: 0.5;
        }

        .item-details {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: ${SPACING_COMPACT};
        }

        .item-name {
          font-size: 1.125rem;
          font-weight: 700;
          color: var(--color-text-primary);
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .item-name:hover {
          color: var(--color-accent);
        }

        .item-modifiers {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_TIGHT};
        }

        .modifier-text {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .item-meta {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .meta-separator {
          margin: 0 ${SPACING_TIGHT};
        }

        .item-actions {
          display: flex;
          align-items: center;
          gap: ${SPACING_CARD};
          margin-top: ${SPACING_COMPACT};
        }

        .quantity-controls {
          display: flex;
          align-items: center;
          gap: ${SPACING_COMPACT};
          border: 1px solid var(--color-border);
          border-radius: ${SPACING_COMPACT};
          padding: ${SPACING_TIGHT};
          background-color: var(--color-surface-muted);
        }

        .quantity-button {
          width: 28px;
          height: 28px;
          border: none;
          background-color: transparent;
          color: var(--color-text-primary);
          font-size: 18px;
          cursor: pointer;
          border-radius: ${SPACING_TIGHT};
          transition: background-color 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .quantity-button:hover {
          background-color: var(--color-border);
        }

        .quantity-display {
          min-width: 32px;
          text-align: center;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .remove-button {
          padding: ${SPACING_COMPACT};
          background-color: transparent;
          border: none;
          color: var(--color-text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .remove-button:hover {
          color: #ef4444;
        }

        .trash-icon {
          width: 20px;
          height: 20px;
        }

        .item-total {
          flex-shrink: 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .cart-summary {
          display: flex;
          justify-content: flex-end;
        }

        .summary-card {
          width: 100%;
          max-width: 400px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: ${SPACING_BUTTON};
        }

        .summary-label {
          font-size: 1rem;
          color: var(--color-text-secondary);
        }

        .summary-value {
          font-size: 1rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .total-row {
          padding-top: ${SPACING_BUTTON};
          border-top: 2px solid var(--color-border);
          margin-top: ${SPACING_BUTTON};
        }

        .total-row .summary-label,
        .total-row .summary-value {
          font-size: 1.125rem;
          font-weight: 700;
        }

        .checkout-button {
          width: 100%;
          padding: ${SPACING_BUTTON} ${SPACING_CARD_BODY};
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          border: none;
          border-radius: ${SPACING_BUTTON};
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease;
          margin-top: ${SPACING_CARD_BODY};
        }

        .checkout-button:not(:disabled):hover {
          background-color: var(--color-accent-hover);
        }

        .checkout-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .checkout-note {
          text-align: center;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          margin-top: ${SPACING_BUTTON};
          margin-bottom: 0;
        }

        @media (max-width: 768px) {
          .item-content {
            flex-direction: column;
          }

          .item-image-wrapper {
            width: 100%;
            height: 200px;
          }

          .item-total {
            text-align: left;
          }

          .cart-summary {
            justify-content: stretch;
          }

          .summary-card {
            max-width: 100%;
          }
        }

        @media (max-width: 600px) {
          .section-title {
            font-size: 1.75rem;
          }

          .cart-item {
            padding: ${SPACING_CARD};
          }
        }
      `}</style>
    </div>
  );
}

export default CartView;
